import React from 'react';
import { OpportunityItem, OpportunityStatus } from '../models/opportunity';

const NEXT_STATUS: Partial<Record<OpportunityStatus, OpportunityStatus>> = {
  '2. Viabilidade Técnica Pendente': '3. Proposta em Elaboração',
  '3. Proposta em Elaboração':       '4. Proposta Enviada',
  '4. Proposta Enviada':             '5. Ganho / Aceite',
};

const STATUS_COLOR: Record<string, string> = {
  '1. Levantamento Inicial':           '#8892AA',
  '2. Viabilidade Técnica Pendente':   '#F59E0B',
  '3. Proposta em Elaboração':         '#60A5FA',
  '4. Proposta Enviada':               '#A78BFA',
  '5. Ganho / Aceite':                 '#22C55E',
  '6. Perdido / Cancelado':            '#EF4444',
};

interface Props {
  item: OpportunityItem;
  currentUserEmail: string;
  onClose: () => void;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus) => Promise<void>;
}

const Row: React.FC<{ label: string; value?: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
    <span style={{ fontSize: 12, color: 'var(--chk-muted)', minWidth: 140 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: accent ? '#22C55E' : 'var(--chk-text)', textAlign: 'right' }}>{value || '—'}</span>
  </div>
);

export const OpportunityDetailModal: React.FC<Props> = ({ item, currentUserEmail, onClose, onAdvanceStatus }) => {
  const [advancing, setAdvancing] = React.useState(false);
  const [confirmStatus, setConfirmStatus] = React.useState<OpportunityStatus | null>(null);

  const isComercial = item.comercialEmail?.toLowerCase() === currentUserEmail.toLowerCase();
  const nextStatus = NEXT_STATUS[item.statusOportunidade];
  const statusColor = STATUS_COLOR[item.statusOportunidade] || '#8892AA';
  const historico = (item.historicoParecer || '').split('\n\n---\n\n').filter(e => e.trim());

  const handleAdvance = async () => {
    if (!confirmStatus) return;
    setAdvancing(true);
    try {
      await onAdvanceStatus(item.id, confirmStatus);
      onClose();
    } finally {
      setAdvancing(false);
      setConfirmStatus(null);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--chk-border)', background: 'var(--chk-navy)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--chk-white)' }}>{item.codigoOportunidade}</div>
            <div style={{ fontSize: 13, color: 'var(--chk-muted)', marginTop: 4 }}>🏢 {item.cliente}</div>
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44` }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
              {item.statusOportunidade}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Secção Comercial */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--chk-red)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>📋 Informação Comercial</div>
            <Row label="Comercial" value={item.comercialNome} />
            <Row label="Email Comercial" value={item.comercialEmail} />
            <Row label="Cliente" value={item.cliente} />
            <Row label="Contacto Cliente" value={item.contactoCliente} />
            <Row label="Data Aceitação" value={item.dataAceitacao} accent={!!item.dataAceitacao} />
          </div>

          {/* Âmbito */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--chk-blue)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>📝 Âmbito / Necessidades</div>
            <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--chk-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {item.ambitoNecessidades || '—'}
            </div>
          </div>

          {/* Secção Técnica */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>🔧 Avaliação Técnica</div>
            <Row label="Técnico DTI" value={item.responsavelTecnicoNome} />
            <Row label="Email Técnico" value={item.responsavelTecnicoEmail} />
            <Row label="Status Técnico" value={item.statusTecnico} />
            <Row label="Data Limite Parecer" value={item.dataLimiteParecer} />
            {item.parecerTecnico && (
              <div style={{ marginTop: 10, background: 'rgba(255,255,255,.03)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Último Parecer</div>
                <div style={{ fontSize: 13, color: 'var(--chk-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{item.parecerTecnico}</div>
              </div>
            )}
          </div>

          {/* Histórico */}
          {historico.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>🕐 Histórico de Pareceres ({historico.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {historico.map((entry, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderLeft: '3px solid #1755B8', padding: '10px 12px', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--chk-text)', lineHeight: 1.5 }}>
                    {entry}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer — só para comercial e se há próximo status */}
        {isComercial && nextStatus && item.statusOportunidade !== '5. Ganho / Aceite' && item.statusOportunidade !== '6. Perdido / Cancelado' && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--chk-border)', background: 'rgba(0,0,0,.2)' }}>
            {confirmStatus ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, color: 'var(--chk-text)' }}>
                  Confirmar avanço para <b style={{ color: STATUS_COLOR[confirmStatus] || '#fff' }}>{confirmStatus}</b>?
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setConfirmStatus(null)} style={{ flex: 1, background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-muted)', borderRadius: 8, padding: '9px', fontSize: 13, cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={handleAdvance} disabled={advancing} style={{ flex: 2, background: 'linear-gradient(135deg,#1755B8,#0F3F8C)', color: 'white', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: advancing ? 'not-allowed' : 'pointer' }}>
                    {advancing ? '⏳ A avançar...' : `✅ Confirmar → ${confirmStatus}`}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmStatus(nextStatus)}
                  style={{ flex: 1, background: 'linear-gradient(135deg,#1755B8,#0F3F8C)', color: 'white', border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  ➡️ Avançar para: {nextStatus}
                </button>
                <button
                  onClick={() => setConfirmStatus('6. Perdido / Cancelado')}
                  style={{ background: 'rgba(239,68,68,.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  ❌ Perdido
                </button>
              </div>
            )}
          </div>
        )}

        {/* Badge Ganho */}
        {item.statusOportunidade === '5. Ganho / Aceite' && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--chk-border)', background: 'rgba(34,197,94,.08)', textAlign: 'center', color: '#22C55E', fontSize: 14, fontWeight: 600 }}>
            🏆 Oportunidade Ganha!
          </div>
        )}
      </div>
    </div>
  );
};
